import {useEffect, useMemo, useState} from 'react';
import {Button} from '@dabafinance/react-components';
import styles from './startup.module.scss';
import {Input, Table, Tooltip} from 'antd';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import DeviceUpload from './profile/DeviceUpload';
import CompanyLogo from './components/CompanyLogo';
import {useQuery} from '@apollo/client';
import DBModal from 'components/modal/modal';
import ViewCompanyRecommendation from './components/recommendation/ViewCompanyRecommendation';
import {
  GET_ALL_COMPANIES,
  GET_ALL_COMPANY_RECOMMENDATIONS,
} from 'graphql/queries/company';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {NavTabsEnum} from 'utils/constants';

const PAGE_SIZE = 20;

const countFormatter = new Intl.NumberFormat('en-US');

/**
 * Capital raised, written the way an amount of money is read.
 *
 * `null` and `0` are different facts and used to render identically as
 * "$0.00": most companies on this list simply have no figure recorded, and
 * printing a zero for them asserted something the data never said.
 */
const formatRaised = amount => {
  if (amount == null) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
};

/** `PRE_SERIES_B` is a database value, not a label. */
const formatStage = stage => {
  if (!stage) return null;
  if (stage.toUpperCase() === 'IPO') return 'IPO';
  return stage
    .split('_')
    .map(word =>
      word.length <= 1
        ? word.toUpperCase()
        : word[0].toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ')
    .replace(/^Pre Series/, 'Pre-Series')
    .replace(/^Pre Seed/, 'Pre-Seed')
    .replace(
      /Series ([a-e])$/i,
      (_, letter) => `Series ${letter.toUpperCase()}`,
    );
};

/**
 * Most records store the legal name as the display name in caps, so printing
 * both stacked one company name twice per row. Only a genuinely different
 * legal entity earns the second line.
 */
const distinctLegalName = record => {
  const {name, legalName} = record;
  if (!legalName) return null;
  const normalise = value => value.replace(/[^a-z0-9]/gi, '').toLowerCase();
  return normalise(legalName) === normalise(name || '') ? null : legalName;
};

const Empty = ({searching, onClear}) => (
  <div className={styles.empty}>
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </svg>
    <h4>{searching ? 'No companies match that search' : 'No companies yet'}</h4>
    <p>
      {searching
        ? 'Try a shorter term — the search matches on company name.'
        : 'Companies you add will be listed here.'}
    </p>
    {searching && (
      <button type="button" className={styles.emptyAction} onClick={onClear}>
        Clear search
      </button>
    )}
  </div>
);

const Startup = () => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchItem, setSearchItem] = useState('');
  const [page, setPage] = useState(1);

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Startups),
  );

  // Typing used to fire one network round trip per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchItem(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {data: recommendationData} = useQuery(GET_ALL_COMPANY_RECOMMENDATIONS, {
    onError: error => toast.error(error.message),
  });

  const {data: company, loading} = useQuery(GET_ALL_COMPANIES, {
    variables: {
      filters: {
        pagination: {limit: PAGE_SIZE, page},
        company: {name: searchItem},
      },
    },
    fetchPolicy: 'no-cache',
    // `toast.error(error)` printed "[object Object]" — Apollo hands back an
    // ApolloError, not a string.
    onError: error => toast.error(error.message),
  });

  const result = company?.getAllAdminCompanies;
  const companies = result?.companies ?? [];

  const recommendations =
    recommendationData?.getAllCompanyRecommendations ?? [];
  const pendingRecommendations = recommendations.filter(item => !item?.isAdded);

  const totalCompanies = result?.statistics?.totalCompanies;

  /**
   * Searching returns every match in one response — the server ignores `limit`
   * once a name is supplied, so page 2 of a search is byte-for-byte page 1.
   * The old screen left Previous/Next on screen and disabled them, which read
   * as "there is more, you just can't reach it". There isn't: this is the
   * whole result set, so the pager steps aside for a match count instead.
   */
  const searching = !!searchItem;
  const showPager = !searching && (result?.hasNextPage || result?.hasPrevPage);

  const rangeLabel = useMemo(() => {
    if (loading && !companies.length) return 'Loading…';
    if (searching)
      return `${countFormatter.format(companies.length)} ${
        companies.length === 1 ? 'match' : 'matches'
      } for “${searchItem}”`;
    if (!companies.length) return null;
    const first = (page - 1) * PAGE_SIZE + 1;
    return `Showing ${countFormatter.format(first)}–${countFormatter.format(
      first + companies.length - 1,
    )} of ${countFormatter.format(result?.totalDocs ?? 0)}`;
  }, [
    loading,
    searching,
    companies.length,
    searchItem,
    page,
    result?.totalDocs,
  ]);

  const columns = [
    {
      title: 'Company',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div className={styles.companyCell}>
          <CompanyLogo src={record.logoImgURL} name={record.name} />
          <div className={styles.companyText}>
            <span className={styles.companyName}>{record.name}</span>
            {distinctLegalName(record) && (
              <span className={styles.companyLegal}>
                {distinctLegalName(record)}
              </span>
            )}
          </div>
          {record.isTrending && (
            <Tooltip title="Trending">
              <span className={styles.trendingDot} aria-label="Trending" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Ticker',
      dataIndex: 'ticker',
      key: 'ticker',
      width: 130,
      render: ticker =>
        ticker ? (
          <span className={styles.ticker}>{ticker}</span>
        ) : (
          <span className={styles.blank}>—</span>
        ),
    },
    {
      title: 'Raised',
      dataIndex: 'totalCapitalRaised',
      key: 'amount',
      width: 130,
      align: 'right',
      render: amount => {
        const formatted = formatRaised(amount);
        return formatted ? (
          <span className={styles.amount}>{formatted}</span>
        ) : (
          <Tooltip title="No figure recorded">
            <span className={styles.blank}>—</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Stage',
      dataIndex: 'stage',
      key: 'stage',
      width: 150,
      render: stage => {
        const label = formatStage(stage);
        return label ? (
          <span className={styles.stagePill}>{label}</span>
        ) : (
          <span className={styles.blank}>—</span>
        );
      },
    },
    {
      title: 'Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: value =>
        value ? (
          <Tooltip title={moment(value).format('D MMM YYYY, HH:mm')}>
            <span className={styles.added}>
              {moment(value).format('D MMM YYYY')}
            </span>
          </Tooltip>
        ) : (
          <span className={styles.blank}>—</span>
        ),
    },
    {
      title: '',
      dataIndex: 'operation',
      key: 'actions',
      width: 150,
      align: 'right',
      render: (text, record) => (
        <Button
          onClick={() =>
            navigate(`/dashboard/opportunities/startups/profile/${record.id}`)
          }
          disabled={!pageOperations?.view}
          type="secondary"
          className={styles.rowButton}
          label="View details"
        />
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.pageHead}>
        <div>
          <h1>Companies</h1>
          <p className={styles.pageSub}>
            {totalCompanies == null
              ? 'Every company on the platform'
              : `${countFormatter.format(
                  totalCompanies,
                )} companies on the platform`}
          </p>
        </div>
        <Button
          label="Add a Company"
          className={styles.headerButton}
          disabled={!pageOperations?.create}
          onClick={() =>
            navigate('/dashboard/opportunities/startups/create-profile')
          }
        />
      </header>

      {/*
        This row used to hold four tiles, two of which were hard-coded to zero:
        "Active campaigns" and "All Companies Listed" had no data source behind
        them and had never shown anything but 0. A tile that always reads zero
        is worse than an absent one — it states a fact.

        What is left is measured: the total from the query's own statistics, and
        recommendations counted by whether they still need a decision, which is
        the number that actually moves when someone works the queue.
      */}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Total companies</span>
          <span className={styles.statValue}>
            {totalCompanies == null
              ? '—'
              : countFormatter.format(totalCompanies)}
          </span>
          <span className={styles.statHint}>across all exchanges</span>
        </div>

        <button
          type="button"
          className={`${styles.stat} ${styles.statAction} ${
            pendingRecommendations.length ? styles.statAlert : ''
          }`}
          onClick={() => setOpenViewModal(true)}>
          <span className={styles.statLabel}>Recommendations</span>
          <span className={styles.statValue}>
            {countFormatter.format(pendingRecommendations.length)}
          </span>
          <span className={styles.statHint}>
            {pendingRecommendations.length
              ? 'awaiting review — open'
              : 'nothing awaiting review'}
          </span>
        </button>
      </div>

      <DeviceUpload openModal={openModal} setOpenModal={setOpenModal} />

      <section className={styles.card}>
        <div className={styles.toolbar}>
          <Input
            className={styles.search}
            allowClear
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder="Search companies by name"
            prefix={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.6-3.6" />
              </svg>
            }
          />
          {rangeLabel && <span className={styles.range}>{rangeLabel}</span>}
        </div>

        <Table
          className={styles.table}
          rowKey={record => record.id}
          dataSource={companies}
          loading={loading}
          columns={columns}
          pagination={false}
          locale={{
            emptyText: loading ? (
              <span />
            ) : (
              <Empty searching={searching} onClear={() => setSearchInput('')} />
            ),
          }}
        />

        {showPager && (
          <div className={styles.pager}>
            <span className={styles.pagerCount}>
              Page {countFormatter.format(page)} of{' '}
              {countFormatter.format(result?.totalPages ?? 1)}
            </span>
            <div className={styles.pagerButtons}>
              <button
                type="button"
                className={styles.pagerButton}
                onClick={() => setPage(current => Math.max(1, current - 1))}
                disabled={!result?.hasPrevPage || loading}>
                Previous
              </button>
              <button
                type="button"
                className={styles.pagerButton}
                onClick={() => setPage(current => current + 1)}
                disabled={!result?.hasNextPage || loading}>
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      <DBModal
        isOpen={openViewModal}
        width={720}
        handleClose={() => setOpenViewModal(false)}
        content={<ViewCompanyRecommendation data={recommendations} />}
      />
    </div>
  );
};

export default Startup;
